import type { ComponentType } from 'react';

/**
 * Route manifest consumed by:
 *   1. `App.tsx` — at runtime, to build React Router lazy routes.
 *   2. `vite.config.ts` — at build time, to feed `vite-plugin-sitemap` (via
 *      `src/lib/static-routes.ts`'s `STATIC_ROUTES`).
 *
 * Adding a route here means you ALSO update the `STATIC_ROUTES` list in
 * `src/lib/static-routes.ts` if the route is publicly indexable. Dynamic /
 * parameterized routes belong here but NOT in the sitemap. The two lists are
 * checked against each other by `src/lib/__tests__/static-routes.test.ts`,
 * not by anything at runtime.
 */

export type SitemapMeta = {
  changefreq?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  priority?: number;
  lastmod?: string;
};

export type RouteDef = {
  path: string;
  lazy: () => Promise<{ default: ComponentType }>;
  /** When omitted, the route is excluded from the sitemap. */
  sitemap?: SitemapMeta;
};

export const routes: RouteDef[] = [
  {
    path: '/',
    lazy: () => import('./routes/home/index'),
    sitemap: { changefreq: 'weekly', priority: 1.0 },
  },
  {
    path: '/legal/privacy',
    lazy: () => import('./routes/legal/privacy'),
    sitemap: { changefreq: 'monthly', priority: 0.6 },
  },
  {
    path: '/legal/terms',
    lazy: () => import('./routes/legal/terms'),
    sitemap: { changefreq: 'monthly', priority: 0.6 },
  },
  {
    path: '/legal/oss',
    lazy: () => import('./routes/legal/oss'),
    sitemap: { changefreq: 'monthly', priority: 0.3 },
  },
  {
    path: '/legal/marketing',
    lazy: () => import('./routes/legal/marketing'),
    sitemap: { changefreq: 'monthly', priority: 0.3 },
  },
  {
    path: '/business',
    lazy: () => import('./routes/business/index'),
    sitemap: { changefreq: 'monthly', priority: 0.8 },
  },

  // Legacy redirects — pages removed 2026-06-08, kept as redirects so old URLs
  // don't 404 (not indexed). about → home, download → #cta, contact → #contact.
  { path: '/about', lazy: () => import('./routes/about/index') },
  { path: '/download', lazy: () => import('./routes/download/index') },
  { path: '/contact', lazy: () => import('./routes/contact/index') },

  // Dynamic / non-indexable routes ----------------------------------------
  // Admin-published terms kinds without a dedicated static page (e.g.
  // health_data, custom categories). The static /legal/* routes above outrank
  // this by React Router's specificity, so they keep their indexed pages.
  { path: '/legal/:slug', lazy: () => import('./routes/legal/document') },
  { path: '/open', lazy: () => import('./routes/open/index') },
  { path: '/open/*', lazy: () => import('./routes/open/slug') },
  { path: '/invite/:code', lazy: () => import('./routes/invite/index') },
  { path: '/promo/:campaign', lazy: () => import('./routes/promo/campaign') },
  { path: '/500', lazy: () => import('./routes/server-error') },
];
